import { IMessageContext } from "@saleor/components/messages";
import { UseNotifierResult } from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { ApolloError } from "apollo-client";
import Cookies from "js-cookie";
import { IntlShape } from "react-intl";

import { isJwtError, isTokenExpired } from "./errors";

export enum TOKEN_STORAGE_KEY {
  AUTH = "auth",
  CSRF = "csrf",
  DEL_TYPES = "del_types"
}

interface TokenCookiesProps {
  auth: string;
  csrf: string;
  userId: string;
}

interface TokenCookies {
  auth: string | null;
  csrf: string | null;
}

export const getTokens = (): TokenCookies => ({
  auth:
    localStorage.getItem(TOKEN_STORAGE_KEY.AUTH) ||
    sessionStorage.getItem(TOKEN_STORAGE_KEY.AUTH),
  csrf:
    localStorage.getItem(TOKEN_STORAGE_KEY.CSRF) ||
    sessionStorage.getItem(TOKEN_STORAGE_KEY.CSRF)
});

export const setTokens = (auth: string, csrf: string, persist: boolean) => {
  if (persist) {
    localStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
    localStorage.setItem(TOKEN_STORAGE_KEY.CSRF, csrf);
  } else {
    sessionStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
    sessionStorage.setItem(TOKEN_STORAGE_KEY.CSRF, csrf);
  }
};

export const setAuthToken = (auth: string, persist: boolean) => {
  if (persist) {
    localStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
  } else {
    sessionStorage.setItem(TOKEN_STORAGE_KEY.AUTH, auth);
  }
};

export const removeTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEY.AUTH);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.AUTH);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.CSRF);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY.DEL_TYPES);
};

export const displayDemoMessage = (
  intl: IntlShape,
  notify: UseNotifierResult
) => {
  notify({
    text: intl.formatMessage(commonMessages.demo)
  });
};

export async function handleQueryAuthError(
  error: ApolloError,
  notify: IMessageContext,
  tokenRefresh: () => Promise<boolean>,
  logout: () => void,
  intl: IntlShape
) {
  if (error.graphQLErrors.some(isJwtError)) {
    if (error.graphQLErrors.every(isTokenExpired)) {
      const success = await tokenRefresh();

      if (!success) {
        logout();
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.sessionExpired)
        });
      }
    } else {
      logout();
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.somethingWentWrong)
      });
    }
  } else if (
    !error.graphQLErrors.every(
      err => err.extensions?.exception?.code === "PermissionDenied"
    )
  ) {
    notify({
      status: "error",
      text: intl.formatMessage(commonMessages.somethingWentWrong)
    });
  }
}

const parseUserId = (): string[] => {
  let storedUserIds = sessionStorage.getItem("userIds");

  if (storedUserIds) {
    try {
      storedUserIds = JSON.parse(storedUserIds);

      if (Array.isArray(storedUserIds)) {
        return storedUserIds;
      }
    } catch (error) {
      console.error("Failed to parse userId from sessionStorage:", error);
    }
  }

  return [];
};

export const setTokenCookies = ({ auth, csrf, userId }: TokenCookiesProps) => {
  Cookies.set(`token-${TOKEN_STORAGE_KEY.AUTH}-${userId}`, auth);
  Cookies.set(`token-${TOKEN_STORAGE_KEY.CSRF}-${userId}`, csrf);

  sessionStorage.setItem("currentUserId", userId);

  const storedUserIds = parseUserId();

  if (Array.isArray(storedUserIds) && !storedUserIds.includes(userId)) {
    storedUserIds.push(userId);
  }

  sessionStorage.setItem("userIds", JSON.stringify(storedUserIds));
};

export const removeTokenCookies = () => {
  const storedUserIds = parseUserId();

  storedUserIds.forEach(userId => {
    Cookies.remove(`token-${TOKEN_STORAGE_KEY.CSRF}-${userId}`);
    Cookies.remove(`token-${TOKEN_STORAGE_KEY.AUTH}-${userId}`);
  });

  sessionStorage.removeItem("currentUserId");
  sessionStorage.removeItem("userIds");
};

export const getTokenCookies = (): TokenCookies => {
  const storedUserIds = parseUserId();
  const currentUserId = sessionStorage.getItem("currentUserId");
  if (currentUserId && storedUserIds.includes(currentUserId)) {
    const auth = Cookies.get(
      `token-${TOKEN_STORAGE_KEY.AUTH}-${currentUserId}`
    );
    const csrf = Cookies.get(
      `token-${TOKEN_STORAGE_KEY.AUTH}-${currentUserId}`
    );
    return { auth, csrf };
  } else {
    return { auth: null, csrf: null };
  }
};
